// Address parameters that never go to Google, and the subset that never goes
// to Meta either (26 Oct 2026).
// Sign-in and payment keys: never to Google or Meta.
//   token, setup_token  the customer hub's sign-in key (/hub?token=) and the
//                       request form's (/looking-for/...?token=); also the
//                       password reset link's key (/signin?mode=reset&token=)
//   email               the password reset link's email address
//   code                Google sign-in's one-time code (/signin?provider=google&code=)
//   t                   a gift card's key (/gift-card?t=)
//   hub_token           the same key on the way to a booking page after sign-in
//                       (/book/...?hub_token=; SignInPage)
//   payment_intent_client_secret, setup_intent_client_secret
//                       Stripe's secrets on the gift card payment return (index.html
//                       also takes them out of the address before any tag starts)
// What a customer searched for: never to Google (Meta keeps getting results
// pages as before: Rithik, 26 Oct 2026).
//   where               what the customer typed in the search (maybe their address)
//   lat, lng            their position after "Use my location"
// A parameter whose value is an address (?redirect=/hub?token=...) keeps its
// place with that address's own parameters removed. Everything else stays as
// it is: utm_*, gclid, gbraid, wbraid, fbclid, service ... (ad attribution
// depends on them).
//
// index.html's Google tag script has the same lists and the same rules (keep
// them in step: src/utils/privateUrlParams.test.js runs both).
export const KEY_URL_PARAMS = Object.freeze([
  "token",
  "setup_token",
  "hub_token",
  "email",
  "code",
  "t",
  "payment_intent_client_secret",
  "setup_intent_client_secret",
]);
export const SEARCH_URL_PARAMS = Object.freeze(["where", "lat", "lng"]);
export const PRIVATE_URL_PARAMS = Object.freeze([...KEY_URL_PARAMS, ...SEARCH_URL_PARAMS]);

// Addresses inside addresses (?redirect=/signin?redirect=/hub?token=...).
const MAX_NESTING = 3;

function decodePart(value) {
  try {
    return decodeURIComponent(String(value).replace(/\+/g, " "));
  } catch {
    return String(value);
  }
}

function isListed(key, list) {
  return list.includes(decodePart(key).trim().toLowerCase());
}

export function isPrivateUrlParam(key) {
  return isListed(key, PRIVATE_URL_PARAMS);
}

// The address without the listed parameters; the very same string when it has
// none (the other parameters keep their exact spelling and order).
function withoutParams(href, list, nesting = 0) {
  const value = String(href ?? "");
  const hashAt = value.indexOf("#");
  const beforeHash = hashAt === -1 ? value : value.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : value.slice(hashAt);
  const queryAt = beforeHash.indexOf("?");
  if (queryAt === -1) return value;
  let changed = false;
  const kept = [];
  for (const part of beforeHash.slice(queryAt + 1).split("&")) {
    const equalsAt = part.indexOf("=");
    const key = equalsAt === -1 ? part : part.slice(0, equalsAt);
    if (isListed(key, list)) {
      changed = true;
      continue;
    }
    if (equalsAt !== -1 && nesting < MAX_NESTING) {
      const inner = decodePart(part.slice(equalsAt + 1));
      if (inner.includes("?")) {
        const cleaned = withoutParams(inner, list, nesting + 1);
        if (cleaned !== inner) {
          changed = true;
          kept.push(key + "=" + encodeURIComponent(cleaned));
          continue;
        }
      }
    }
    kept.push(part);
  }
  if (!changed) return value;
  return beforeHash.slice(0, queryAt) + (kept.length ? "?" + kept.join("&") : "") + hash;
}

// What Google gets: no keys and nothing the customer searched for.
export function withoutPrivateUrlParams(href) {
  return withoutParams(href, PRIVATE_URL_PARAMS);
}

export function hasPrivateUrlParams(href) {
  const value = String(href ?? "");
  return withoutParams(value, PRIVATE_URL_PARAMS) !== value;
}

// A sign-in or payment key in the address (Meta gets nothing then).
export function hasKeyUrlParams(href) {
  const value = String(href ?? "");
  return withoutParams(value, KEY_URL_PARAMS) !== value;
}
